/**
 * Forecasts & MSI — the inshore-waters forecast and the shipping forecast (the
 * MSI broadcast equivalent), captured from RYA eNav as training content. Read-only
 * text panels; the synoptic charts, GFS and live observations are images on eNav,
 * not data, so they aren't shown here.
 */
import { useState } from 'react';
import { Sheet } from './Sheet';
import { SHIPPING_FORECASTS, INSHORE_FORECASTS } from '@/weather/enavForecasts';

type Kind = 'inshore' | 'shipping';

export function ForecastSheet() {
  const [kind, setKind] = useState<Kind>('inshore');
  const [shipIdx, setShipIdx] = useState(0);

  const inshore = INSHORE_FORECASTS[0];
  const ship = SHIPPING_FORECASTS[Math.min(shipIdx, SHIPPING_FORECASTS.length - 1)];

  return (
    <Sheet
      name="forecast"
      title="Forecasts & MSI"
      sub="RYA eNav training forecasts for the training area. Issued by the Weather Office, on behalf of RYA Training. Not for real-world navigation."
    >
      <div className="acp-seg">
        <button type="button" className={kind === 'inshore' ? 'on' : ''} onClick={() => setKind('inshore')}>
          Inshore waters
        </button>
        <button type="button" className={kind === 'shipping' ? 'on' : ''} onClick={() => setKind('shipping')}>
          Shipping (MSI)
        </button>
      </div>

      {kind === 'inshore' ? (
        inshore ? (
          <>
            <div className="acp-kv">
              <span className="k">Issued</span>
              <span className="v">{inshore.issuedAt}</span>
            </div>
            <label className="acp-fieldlabel">General situation</label>
            <p className="sub">{inshore.generalSituation}</p>
            <label className="acp-fieldlabel">{inshore.scope}</label>
            <p className="acp-note">For {inshore.period}.</p>
            {inshore.areas.map((a, i) => (
              <div key={i} style={{ marginTop: 10 }}>
                <b>{a.name}</b>
                <p className="sub" style={{ margin: '3px 0' }}>
                  24 hours: {a.forecast24h}
                </p>
                <p className="sub" style={{ margin: '3px 0' }}>
                  Outlook: {a.outlook}
                </p>
              </div>
            ))}
          </>
        ) : (
          <p className="acp-empty">No inshore-waters forecast captured.</p>
        )
      ) : ship ? (
        <>
          {SHIPPING_FORECASTS.length > 1 && (
            <div className="acp-seg" style={{ flexWrap: 'wrap' }}>
              {SHIPPING_FORECASTS.map((f, i) => (
                <button key={i} type="button" className={shipIdx === i ? 'on' : ''} onClick={() => setShipIdx(i)}>
                  {f.date}
                </button>
              ))}
            </div>
          )}
          <div className="acp-kv">
            <span className="k">Issued</span>
            <span className="v">{ship.issuedAt}</span>
          </div>
          <label className="acp-fieldlabel">General synopsis</label>
          <p className="sub">{ship.synopsis}</p>
          <p className="acp-note">For {ship.period}.</p>
          {ship.areas.map((a, i) => (
            <div key={i} style={{ marginTop: 10 }}>
              <b>{a.name}</b>
              <p className="sub" style={{ margin: '3px 0' }}>
                {a.forecast}
              </p>
            </div>
          ))}
        </>
      ) : (
        <p className="acp-empty">No shipping forecast captured.</p>
      )}

      <p className="acp-note" style={{ marginTop: 12 }}>
        Synoptic charts, GFS and live observations are images on the eNav site, not data, so they
        aren’t reproduced here.
      </p>
    </Sheet>
  );
}
